import { useState, useCallback, useContext, useEffect, useMemo } from 'react';
import { LocalizationContext } from 'app/localization/Context';
import useLocalStorage from './LocalStorage';
import momentj from 'moment-jalaali';

export default function useLogList(home, negotiatedProtocol, isAuthenticated, sendEvent) {
    const l = useContext(LocalizationContext);
    const [showSecondsInLog, setShowSecondsInLog] = useLocalStorage('showSecondsInLog', false);
    const logFilter = home.logFilter;
    const setLogFilter = home.setLogFilter;

    const [logData, setLogData] = useState({});

    const hasNewLogV1 = negotiatedProtocol.hasNewLogV1;
    const hasNewLogV2 = negotiatedProtocol.hasNewLogV2;
    const supportLogFilter = negotiatedProtocol.supportLogFilter;
    const supportLogBeforeId = negotiatedProtocol.supportLogBeforeId;

    const [areLogsFetched, logs] = useMemo(() => {
        // check relevancy
        if (isNU(logData.logs)) return [false, []];
        if (hasNewLogV1 || hasNewLogV2) {
            if (logData.language !== l.fullLanguage) {
                console.log("onLogList: mismatch language", logData.language, l.fullLanguage);
                return [false, []];
            }
        }
        if (supportLogFilter) {
            if (!isLogFilterEqual(logData.filter, logFilter)) {
                console.log("onLogList: mismatch logFilter", logData.filter, logFilter);
                return [false, []];
            }
        }
        return [true, logData.logs];
    }, [logData, l.fullLanguage, logFilter, hasNewLogV1, hasNewLogV2, supportLogFilter]);

    const onLogList = useCallback((message) => {
        // check relevancy
        if (hasNewLogV1 || hasNewLogV2) {
            if (message.msg.language !== l.fullLanguage) {
                console.log("onLogList: mismatch language", message.msg.language, l.fullLanguage);
                return;
            }
        }
        if (supportLogFilter) {
            if (!isLogFilterEqual(message.msg.filter, logFilter)) {
                console.log("onLogList: mismatch logFilter", message.msg.filter, logFilter);
                return;
            }
        }

        // hardcoded log!
        // message.msg = {
        //     "logs": [
        //         {
        //             "id": 500,
        //             "date": "2018-04-04 16:00:00",
        //             "subject": {
        //                 "name": "محمد",
        //                 "type": 1,
        //                 "icon": 1,
        //             },
        //             "object": {
        //                 "name": "سناریو مسافرت",
        //                 "type": 5,
        //                 "icon": 2,
        //             },
        //             "event": "اجرا شد.",
        //         },
        //         {
        //             "id": 400,
        //             "date": "2018-04-04 15:59:00",
        //             "subject": {
        //                 "name": "محسن",
        //                 "type": 1,
        //                 "icon": 5,
        //             },
        //             "object": {
        //                 "name": "سارا خانوم",
        //                 "type": 2,
        //                 "icon": 0
        //                 // "device_type": 1,
        //             },
        //             "event": "ادمین شد.",
        //         },
        //         {
        //             "id": 300,
        //             "date": "2018-04-04 15:59:00",
        //             "object": {
        //                 "name": "آژیر",
        //                 "type": 1,
        //                 "device_type": 13,
        //             },
        //             "event": "آژیر زد.",
        //         },
        //         // {
        //         //     "id": 200,
        //         //     "date": "2018-04-04 15:59:00",
        //         //     "subject": {
        //         //         "name": "Mohsen",
        //         //         "type": 1,
        //         //         "icon": 1,
        //         //     },
        //         //     "object": {
        //         //         "name": "User +989123***1234",
        //         //         "type": 2,
        //         //     },
        //         //     "event": "Was invited to the home",
        //         //     "redacted": true,
        //         // },
        //     ],
        //     "language": "fa",
        //     "filter": {}
        // }

        // process log list
        let shoudAppend;
        if (logs.length > 0) {
            if (supportLogBeforeId) {
                let lastIdOfMyLogList = logs[logs.length - 1].id;
                shoudAppend = !isNU(message.msg.before_id) && (message.msg.before_id === lastIdOfMyLogList)
            } else {
                let firstIdOfMyList = logs[0].id;
                let firstIdOfNewList = message.msg.logs[0].id;
                shoudAppend = firstIdOfNewList < firstIdOfMyList
            }
        } else {
            shoudAppend = false;
        }
        if (shoudAppend) { // append to logs
            let newLogs = [...logs];
            let lastId = logs.length > 0 ? logs[logs.length - 1].id : 0;
            for (let i = 0; i < message.msg.logs.length; i++) {
                const log = message.msg.logs[i];

                if (lastId > log.id) {
                    lastId = log.id;
                    newLogs.push(log);
                }
            }
            setLogData({ logs: newLogs, filter: message.msg.filter, language: message.msg.language });
        } else { // refresh logs
            setLogData({ logs: message.msg.logs, filter: message.msg.filter, language: message.msg.language });
        }
    }, [logs, logFilter, l.fullLanguage, hasNewLogV1, hasNewLogV2, supportLogFilter, supportLogBeforeId]);

    const requestMoreLogs = useCallback(() => {
        if (logs.length > 0) {
            if (supportLogBeforeId) {
                sendEvent('log_list', {
                    before_id: logs[logs.length - 1].id,
                    language: l.fullLanguage,
                    filter: logFilter
                });
            } else {
                sendEvent('log_list_from_id', {
                    id: logs[logs.length - 1].id,
                    language: l.fullLanguage,
                });
            }
        } else {
            sendEvent('log_list', { "language": l.fullLanguage, "filter": logFilter });
        }
    }, [sendEvent, logs, l.fullLanguage, logFilter, supportLogBeforeId]);

    const refreshLogs = useCallback(() => {
        sendEvent('log_list', { "language": l.fullLanguage, "filter": logFilter });
    }, [sendEvent, l.fullLanguage, logFilter]);

    // this will get logs on each reconnection (change of sendEvent)
    useEffect(() => {
        if (isAuthenticated)
            refreshLogs();
    }, [isAuthenticated, refreshLogs])

    return {
        areLogsFetched, logs, onLogList, requestMoreLogs, refreshLogs,
        showSecondsInLog, setShowSecondsInLog, logFilter, setLogFilter
    };
}

function isNU(x) {
    return (x === undefined) || (x === null)
}
function isListEqual(l1, l2) {
    for (let i = 0; i < l1.length; i++) {
        const e1 = l1[i];
        if (!l2.includes(e1)) return false;
    }
    for (let i = 0; i < l2.length; i++) {
        const e2 = l2[i];
        if (!l1.includes(e2)) return false;
    }
    return true;
}

function isLogFilterEqual(f1, f2) {
    const f1IsEmpty = isNU(f1) || (
        isNU(f1.user) && isNU(f1.device) &&
        isNU(f1.bind) && isNU(f1.scenario) && isNU(f1.room) && isNU(f1.time))
    const f2IsEmpty = isNU(f2) || (
        isNU(f2.user) && isNU(f2.device) &&
        isNU(f2.bind) && isNU(f2.scenario) && isNU(f2.room) && isNU(f2.time))

    if (f1IsEmpty !== f2IsEmpty) return false;
    if (!f1IsEmpty) {
        const f1HasUserFilter = !isNU(f1.user) && !isNU(f1.user.id);
        const f2HasUserFilter = !isNU(f2.user) && !isNU(f2.user.id);
        if (f1HasUserFilter !== f2HasUserFilter) return false;
        if (f1HasUserFilter) {
            if (!isListEqual(f1.user.id, f2.user.id)) return false;
        }
        const f1HasDeviceFilter = !isNU(f1.device) && !isNU(f1.device.mac);
        const f2HasDeviceFilter = !isNU(f2.device) && !isNU(f2.device.mac);
        if (f1HasDeviceFilter !== f2HasDeviceFilter) return false;
        if (f1HasDeviceFilter) {
            if (!isListEqual(f1.device.mac, f2.device.mac)) return false;
        }
        const f1HasBindFilter = !isNU(f1.bind) && !isNU(f1.bind.id);
        const f2HasBindFilter = !isNU(f2.bind) && !isNU(f2.bind.id);
        if (f1HasBindFilter !== f2HasBindFilter) return false;
        if (f1HasBindFilter) {
            if (!isListEqual(f1.bind.id, f2.bind.id)) return false;
        }
        const f1HasScenarioFilter = !isNU(f1.scenario) && !isNU(f1.scenario.id);
        const f2HasScenarioFilter = !isNU(f2.scenario) && !isNU(f2.scenario.id);
        if (f1HasScenarioFilter !== f2HasScenarioFilter) return false;
        if (f1HasScenarioFilter) {
            if (!isListEqual(f1.scenario.id, f2.scenario.id)) return false;
        }
        const f1HasRoomFilter = !isNU(f1.room) && !isNU(f1.room.id);
        const f2HasRoomFilter = !isNU(f2.room) && !isNU(f2.room.id);
        if (f1HasRoomFilter !== f2HasRoomFilter) return false;
        if (f1HasRoomFilter) {
            if (!isListEqual(f1.room.id, f2.room.id)) return false;
        }
        const f1HasTimeFilter = !isNU(f1.time) && !isNU(f1.time.start) && !isNU(f1.time.end);
        const f2HasTimeFilter = !isNU(f2.time) && !isNU(f2.time.start) && !isNU(f2.time.end);
        if (f1HasTimeFilter !== f2HasTimeFilter) return false;
        if (f1HasTimeFilter) {
            if (f1.time.start !== f2.time.start) return false;
            if (f1.time.end !== f2.time.end) return false;
        }
    }
    return true
}

export function countsInLogFilterSections(logFilter) {
    const counts = {
        user: 0,
        device: 0,
        bind: 0,
        scenario: 0,
        room: 0,
        time: 0,
    };
    if (logFilter.user) {
        if (logFilter.user.id) {
            counts.user = logFilter.user.id.length
        }
    }
    if (logFilter.device) {
        if (logFilter.device.mac) {
            counts.device = logFilter.device.mac.length
        }
    }
    if (logFilter.bind) {
        if (logFilter.bind.id) {
            counts.bind = logFilter.bind.id.length
        }
    }
    if (logFilter.scenario) {
        if (logFilter.scenario.id) {
            counts.scenario = logFilter.scenario.id.length
        }
    }
    if (logFilter.room) {
        if (logFilter.room.id) {
            counts.room = logFilter.room.id.length
        }
    }
    if (logFilter.time) {
        if (logFilter.time.start) {
            if (logFilter.time.end) {
                const s = momentj(logFilter.time.start, "YYYY-MM-DD HH:mm:ss");
                const e = momentj(logFilter.time.end, "YYYY-MM-DD HH:mm:ss");
                const days = Math.ceil(Math.abs(s.diff(e, 'days', true)));
                counts.time = days > 0 ? days : 1;
            }
        }
    }
    const filterIsEmpty =
        (counts.user === 0) &&
        (counts.device === 0) &&
        (counts.bind === 0) &&
        (counts.scenario === 0) &&
        (counts.time === 0) &&
        (counts.room === 0);
    return { filterIsEmpty, counts };
}

export function clearLogFilterSection(logFilter, setLogFilter, sectionName) {
    const newLogFilter = { ...logFilter };
    delete newLogFilter[sectionName];
    setLogFilter(newLogFilter);
}

export const LogSubjectType = {
    Gateway: 0,
    User: 1,
    Bind: 2,
    Scenario: 3,
    Room: 4,
    Device: 5,
}
